import {
  buildBrandTokens,
  deriveBrandBases,
  type TBrandSeed
} from 'themes/tokens';

/**
 * NewAiEye brand seed — this partner's entire colour identity.
 *
 * Six colours plus the neutrals: `deriveBrandBases` expands them into Home,
 * Bird, Trail, Pet and Baby (see its doc comment for the exact formula), and
 * `buildBrandTokens` expands that into the tonal ramps, page backgrounds and
 * gradients the theme renders.
 *
 * Same palette as Luna today, kept separate so the two can diverge.
 */

/** Light-mode seed. */
const LIGHT_SEED: TBrandSeed = {
  brand: {
    primary: '#6171ffff',
    secondary: '#FF6F61',
    tertiary: '#1ABC9C'
  },
  error: 'rgba(255, 44, 44, 1)',

  // Neutrals.
  gray: '#D9D9D9',
  black: '#000000',

  // Text. Same across partners today; a partner may override.
  text: {
    primary: '#000000',
    secondary: '#666666',
    tertiary: '#FF0000'
  }
};

/** Dark-mode seed. */
const DARK_SEED: TBrandSeed = {
  brand: {
    primary: '#5C6DFF',
    secondary: '#ff6f61',
    tertiary: '#1abc9c'
  },
  error: '#ff2c2cff',

  // Neutrals.
  gray: '#D9D9D9',
  black: '#000000',

  // Dark mode. `primary`/`secondary` reproduce today's actual rendered
  // dark-text colours (gray[600] on this partner's shared neutral seed)
  // except `primary`, now literal white per spec — this field was
  // previously unused, buildDarkPalette ignored it entirely (see palette.ts).
  // `tertiary` stays '#FF0000', same as light: Pet's own brand colour is
  // DERIVED from this exact field (ramp(text.tertiary)[300] — see
  // PRODUCT_SEED_STEPS.pet in palette.ts), so changing it here would have
  // silently changed Pet's dark-mode colour, not just this text role.
  text: {
    primary: '#FFFFFF',
    secondary: '#AEAEAE',
    tertiary: '#FF0000'
  }
};

export const brandLight = buildBrandTokens(
  deriveBrandBases(LIGHT_SEED),
  'light'
);
export const brandDark = buildBrandTokens(deriveBrandBases(DARK_SEED), 'dark');
